import {
  defaultBackgroundColor,
  defaultGravityMultiplier,
  defaultParticleSettings,
  defaultTimeScaleFactor
} from './constants-defaults'
import { ChangeableParticleAttributes, Vector } from './types'
import { ParticleEngine } from './particles'
import { SoundEngine } from './sound/sound-engine'
import { Profiler } from './profiler'
import { SensorService } from './sensors/sensor-service'
import { Renderer } from './renderers/renderer'

export class Controller {
  private pickupMode: boolean = false
  private mousePosition: Vector | undefined
  private pickupRadius: number = 125

  constructor(readonly particleEngine: ParticleEngine, readonly soundEngine: SoundEngine, readonly performance: Profiler, readonly renderer: Renderer, readonly sensorService: SensorService) {
    this.setupControls()
  }

  private setupOpenCloseButton(buttonId: string, targetId: string) {
    let currentlyOpen = false

    const openCloseButton = document.getElementById(buttonId)
    const openCloseButtonHandler = (e: Event) => {
      e.preventDefault()
      currentlyOpen = !currentlyOpen

      let targetElement = document.getElementById(targetId)
      const orientation = targetElement?.classList?.contains('orientation-left') ? 'LEFT' : 'RIGHT'
      const getNewIcon = (): '>>' | '<<' => {
        if (orientation === 'LEFT') {
          return currentlyOpen ? '<<' : '>>'
        } else {
          return currentlyOpen ? '>>' : '<<'
        }
      }
      openCloseButton!.innerHTML = getNewIcon()

      targetElement!.classList.remove('ocb-open', 'ocb-closed')
      targetElement!.classList.add(currentlyOpen ? 'ocb-open' : 'ocb-closed')
    }
    openCloseButton?.addEventListener('click', openCloseButtonHandler)
    openCloseButton?.addEventListener('touchstart', openCloseButtonHandler)


  }

  private setupControls() {
    this.setupOpenCloseButton('openclosebutton-controls', 'controls')
    this.setupOpenCloseButton('openclosebutton-diagnostics', 'diagnostics')

    const nrOfParticles = this.createLabeledInput({
      inputId: 'particles',
      labelText: 'Particles',
      initialValue: defaultParticleSettings.nrOfParticles,
      inputType: 'number',
      displayValue: false
    })

    window.addEventListener('keypress', e => [ 'Enter', 'Space' ].includes(e.code) ? spawnFromInputValues() : void (0))

    const lifespan = this.createInputWithRandomness({
      inputId: 'lifespan',
      labelText: 'Lifespan (sec)',
      initialValue: defaultParticleSettings.lifespan.value,
      inputType: 'number',
      displayValue: false
    }, {
      type: 'range',
      initialValue: defaultParticleSettings.lifespan.randomness,
      min: 0,
      max: 100
    })


    const bounce = this.createInputWithRandomness({
      inputId: 'bounce',
      labelText: 'Bounce',
      initialValue: defaultParticleSettings.bounciness.value,
      inputType: 'range',
      min: 0,
      max: 1,
      step: 0.01,
      displayValue: false,
    }, {
      type: 'range',
      initialValue: defaultParticleSettings.bounciness.randomness,
      min: 0,
      max: 100
    })

    const radius = this.createInputWithRandomness({
      inputId: 'radius',
      labelText: 'Radius',
      initialValue: defaultParticleSettings.radius.value,
      inputType: 'range',
      min: 1,
      max: 40,
      step: 1,
      displayValue: false
    }, {
      type: 'range',
      initialValue: defaultParticleSettings.radius.randomness,
      min: 0,
      max: 100
    })

    const spawnButton: HTMLButtonElement = document.createElement('button')
    spawnButton.innerText = 'Spawn'
    spawnButton.addEventListener('click', () => {
      spawnFromInputValues()
    })

    const resetButton = document.createElement('button')
    resetButton.innerText = 'Reset'
    resetButton?.addEventListener('click', () => {
      this.particleEngine.resetParticles()
    })

    const benchButton = document.createElement('button')
    benchButton.innerText = 'Benchmark'
    benchButton.addEventListener('click', () => this.performance.startBenchmark())

    const drawBenchButton = document.createElement('button')
    drawBenchButton.innerText = 'Benchmark (Draw)'
    drawBenchButton.addEventListener('click', () => this.performance.startRenderDrawBench())

    document.getElementById('spawn-controls')?.append(
      nrOfParticles.label,
      nrOfParticles.input,
      lifespan.label,
      lifespan.input,
      lifespan.randomnessLabel!,
      lifespan.randomnessInput!,
      bounce.label,
      bounce.input,
      bounce.randomnessLabel!,
      bounce.randomnessInput!,
      radius.label,
      radius.input,
      radius.randomnessLabel!,
      radius.randomnessInput!,
      spawnButton, resetButton, benchButton, drawBenchButton)

    const {
      getCurrentColor,
      getHueRandomness,
      colorControls: particleColorControls
    } = this.createColorInputs({ createHueRandomness: true, initialValue: defaultParticleSettings.color.value })

    document.getElementById('particle-color-controls')?.append(particleColorControls)

    const {
      colorControls: bgColorControls
    } = this.createColorInputs({
      createHueRandomness: false,
      onColorChanged: (newColor) => this.renderer.backgroundColor = newColor,
      initialValue: defaultBackgroundColor
    })

    document.getElementById('bg-color-controls')?.append(bgColorControls)

    const getCurrentOptions = () => ({
      nrOfParticles: Number.parseFloat(nrOfParticles.input.value),
      lifespan: {
        value: Number.parseFloat(lifespan.input.value),
        randomness: Number.parseFloat(lifespan.randomnessInput!.value)
      },
      bounciness: {
        value: Number.parseFloat(bounce.input.value),
        randomness: Number.parseFloat(bounce.randomnessInput!.value)
      },
      radius: {
        value: Number.parseFloat(radius.input.value),
        randomness: Number.parseFloat(radius.randomnessInput!.value)
      },
      color: {
        value: getCurrentColor(),
        randomness: getHueRandomness()
      }
    })

    const spawnFromInputValues = () => {
      this.particleEngine.spawnParticles(getCurrentOptions())
    }

    this.createCheckbox('Pickup', 'additional-controls', e => this.pickupMode = (e.target as HTMLInputElement).checked, false)
    const pickupRadiusInput = this.createLabeledInput({
      displayValue: true,
      initialValue: 125,
      inputType: 'range',
      inputId: 'pickup-radius',
      labelText: 'Pickup radius',
      max: 500,
      min: 1,
      step: 10
    })
    pickupRadiusInput.input.addEventListener('change', (e: Event) => this.pickupRadius = parseInt((e.target as HTMLInputElement).value))
    document.getElementById('additional-controls')?.append(pickupRadiusInput.label, pickupRadiusInput.input)
    this.createCheckbox('Sound', 'additional-controls', e => this.soundEngine.soundEnabled = (e.target as HTMLInputElement).checked, false)
    this.createCheckbox('Color Transfer', 'additional-controls', e => this.particleEngine.colorTransferEnabled = (e.target as HTMLInputElement).checked, false)
    this.createCheckbox('Color Transfer Mix', 'additional-controls', e => this.particleEngine.colorTransferMode = (e.target as HTMLInputElement).checked ? 'MIX' : 'REPLACE', false)
    this.createCheckbox('Collisions', 'additional-controls', e => this.particleEngine.collisionsEnabled = (e.target as HTMLInputElement).checked, true)
    this.createCheckbox('Debug Stabilization', 'additional-controls', e => this.renderer.debugStabilization = (e.target as HTMLInputElement).checked, false)
    this.createCheckbox('Display Grid', 'additional-controls', e => this.renderer.drawGrid = (e.target as HTMLInputElement).checked, false)
    this.createCheckbox('Visualize Age', 'additional-controls', e => this.renderer.visualizeAge = (e.target as HTMLInputElement).checked, false)
    this.createAdditionalControlSliders()


    this.createMousePositionTracking()
    this.createMouseSpawnControls(getCurrentOptions)
    this.createMouseDragControls()
  }

  private createColorInputs({ createHueRandomness, onColorChanged, initialValue }: {
    createHueRandomness: boolean,
    onColorChanged?: (newColor: string) => void,
    initialValue: string
  }) {
    const wrapper = document.createElement('div') as HTMLDivElement
    wrapper.classList.add('color-input')

    const inputChangeListener = () => {
      if (onColorChanged) {
        onColorChanged(getCurrentColor())
      }
    }

    const colorPicker = document.createElement('input') as HTMLInputElement
    colorPicker.type = 'color'
    colorPicker.value = initialValue

    colorPicker.addEventListener('change', inputChangeListener)

    const {
      input: hueRandomness,
      label: hueRandomnessLabel,
    } = this.createLabeledInput({
      inputId: 'hueRandomness',
      labelText: 'H-Rand',
      initialValue: defaultParticleSettings.color.randomness,
      inputType: 'range',
      min: 0,
      max: 100,
      displayValue: false
    })


    const getCurrentColor = () => colorPicker.value


    wrapper.append(
      colorPicker,
      ...(createHueRandomness ? [ hueRandomnessLabel!, hueRandomness! ] : [])
    )

    return {
      colorControls: wrapper,
      getCurrentColor,
      getHueRandomness: () => Number.parseFloat(hueRandomness.value),
    }
  }

  private createAdditionalControlSliders() {
    const slidersWrapper = document.createElement('div')
    slidersWrapper.id = 'sliders-wrapper'

    const timescaleInputAndLabel = this.createLabeledInput({
      inputId: 'timescale',
      inputType: 'range',
      labelText: 'Timescale',
      initialValue: defaultTimeScaleFactor,
      min: 0,
      max: 1,
      step: 0.01,
      displayValue: true
    })
    timescaleInputAndLabel.input.addEventListener('input', e => this.particleEngine.timeScaleFactor = Number.parseFloat((e.target as HTMLInputElement).value))

    const gravityInputAndLabel = this.createLabeledInput({
      inputId: 'gravity-modifier',
      inputType: 'range',
      labelText: 'Gravity',
      initialValue: defaultGravityMultiplier,
      min: -2,
      max: 2,
      step: 0.1,
      displayValue: true
    })
    gravityInputAndLabel.input.addEventListener('input', e => {
      this.particleEngine.gravityMultiplier = Number.parseFloat((e.target as HTMLInputElement).value)
    })

    slidersWrapper.append(
      timescaleInputAndLabel.label,
      timescaleInputAndLabel.input,
      timescaleInputAndLabel.valueDisplay!,
      gravityInputAndLabel.label,
      gravityInputAndLabel.input,
      gravityInputAndLabel.valueDisplay!,
    )
    document.getElementById('additional-controls')?.append(slidersWrapper)

    this.createCheckbox('Use Sensors', 'additional-controls', async (e) => {
      const target = e.target as HTMLInputElement
      if (target.checked) {
        const permissionRequestResult = await this.sensorService.initSensorData()
        target.checked = permissionRequestResult
        this.particleEngine.useSensors = permissionRequestResult
      } else {
        this.particleEngine.useSensors = false
      }
    }, true)

  }


  private createCheckbox(labelText: string, containerId: string, changeListener: (e: Event) => void, initialValue: boolean) {
    const checkbox = document.createElement('input')
    checkbox.type = 'checkbox'
    checkbox.addEventListener('change', changeListener)
    checkbox.checked = initialValue

    const label = document.createElement('label')
    label.innerText = labelText

    label.appendChild(checkbox)

    document.getElementById(containerId)?.appendChild(label)
    return checkbox
  }


  private createInputWithRandomness({
                                      labelText,
                                      step,
                                      inputType,
                                      inputId,
                                      min,
                                      max,
                                      initialValue,
                                      displayValue
                                    }: InputCreationOptions, randomnessOptions: RandomnessInputCreationOptions): InputCreationOutput {
    const {
      input,
      label
    } = this.createLabeledInput({
      displayValue,
      inputType,
      inputId,
      labelText,
      initialValue,
      min,
      max,
      step
    })


    const {
      input: randomnessInput,
      label: randomnessLabel
    } = this.createLabeledInput({
      inputId: `${ inputId }-randomness`,
      inputType: randomnessOptions.type,
      labelText: 'Rand',
      initialValue: randomnessOptions.initialValue,
      min: randomnessOptions.min,
      max: randomnessOptions.max,
      step: randomnessOptions.step,
      displayValue: false
    })


    return {
      input, label, randomnessInput, randomnessLabel
    }
  }

  private createLabeledInput({
                               displayValue,
                               initialValue,
                               inputType,
                               inputId,
                               labelText,
                               max,
                               min,
                               step
                             }: InputCreationOptions): {
    input: HTMLInputElement,
    label: HTMLLabelElement,
    valueDisplay?: HTMLDivElement
  } {
    const newInput = document.createElement('input')
    newInput.type = inputType
    newInput.id = inputId
    if (min) {
      newInput.min = String(min)
    }
    if (max) {
      newInput.max = String(max)
    }
    if (step) {
      newInput.step = String(step)
    }
    newInput.value = String(initialValue)

    const label = document.createElement('label')
    label.htmlFor = newInput.id
    label.innerText = labelText

    let valueDisplay
    if (displayValue) {
      valueDisplay = document.createElement('div')
      valueDisplay.classList.add('inputValueDisplay')
      newInput.addEventListener('input', e => valueDisplay!.innerText = (e.target as HTMLInputElement).value)
      newInput.dispatchEvent(new Event('input'))
    }

    return { input: newInput, label, valueDisplay }
  }

  private createMousePositionTracking() {
    this.renderer?.canvas.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return
      this.mousePosition = { x: e.clientX, y: e.clientY }
    })

    this.renderer?.canvas.addEventListener('touchstart', (e) => {
      this.mousePosition = { x: e.touches[0].clientX, y: e.touches[0].clientY }
    })

    this.renderer?.canvas.addEventListener('mousemove', (e) => {
      this.mousePosition = { x: e.clientX, y: e.clientY }
    })
    this.renderer?.canvas.addEventListener('touchmove', (e) => {
      this.mousePosition = { x: e.touches[0].clientX, y: e.touches[0].clientY }
    })
  }

  private createMouseSpawnControls(getCurrentOptions: () => ChangeableParticleAttributes) {
    let intervalId: number | undefined

    this.renderer?.canvas.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return
      clearInterval(intervalId)
      intervalId = setInterval(() => spawn(), 10)
    })
    this.renderer?.canvas.addEventListener('touchstart', () => {
      clearInterval(intervalId)
      intervalId = setInterval(() => spawn(), 10)
    })

    document.addEventListener('mouseup', () => clearInterval(intervalId))
    document.addEventListener('touchend', () => clearInterval(intervalId))

    const spawn = () => {
      if (!this.pickupMode && this.mousePosition) {
        const options = getCurrentOptions()
        options.nrOfParticles = Math.floor(options.nrOfParticles / 16)
        this.particleEngine?.spawnParticles(options, this.mousePosition)
      }
    }
  }

  private createMouseDragControls() {
    this.renderer?.canvas.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return
      pickupParticle(this.pickupRadius)
    })
    this.renderer?.canvas.addEventListener('touchstart', () => {
      pickupParticle(this.pickupRadius)
    })

    this.renderer?.canvas.addEventListener('mousemove', (e) => {
      if (this.pickupMode) {
        this.particleEngine.pickedUpParticlesMove({ x: e.clientX, y: e.clientY })
      }
    })
    this.renderer?.canvas.addEventListener('touchmove', (e) => {
      if (this.pickupMode) {
        this.particleEngine.pickedUpParticlesMove({ x: e.touches[0].clientX, y: e.touches[0].clientY })
      }
    })

    document.addEventListener('mouseup', () => dropParticle())
    document.addEventListener('touchend', () => dropParticle())

    const pickupParticle = (pickupRadius: number) => {
      if (this.pickupMode && this.mousePosition) {
        this.particleEngine.pickUpParticles(this.mousePosition, pickupRadius)
      }
    }

    const dropParticle = () => {
      if (this.pickupMode) {
        this.particleEngine.releaseParticles()
      }
    }
  }
}


type InputCreationOptions = {
  inputType: string
  inputId: string
  initialValue: number
  labelText: string
  displayValue: boolean
  min?: number
  max?: number
  step?: number
}

type RandomnessInputCreationOptions = {
  type: string
  initialValue: number
  min?: number
  max?: number
  step?: number
}

type InputCreationOutput = {
  input: HTMLInputElement,
  label: HTMLLabelElement,
  randomnessInput?: HTMLInputElement
  randomnessLabel?: HTMLLabelElement
}
